import * as React from 'react';
import styled from 'styled-components';
import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import { horizontalSpace, verticalSpace } from '@doltech/utils/lib/css-style';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { IconSystemV2SpecificPenCurve } from '@doltech/icons/SystemV2/IconSystemV2SpecificPenCurve';
import { AchievementItemProps, AuthorAchievementInfo } from './AuthorAchievementInfo';
import { TextLink } from '@doltech/ui/lib/figma/TextLink/TextLink';
import { IconSystemV2ArrowChevronDownArrowDown } from '@doltech/icons/SystemV2/IconSystemV2ArrowChevronDownArrowDown';
import { fromScreen } from '@doltech/core/lib/responsive/responsive.util';
import { IconSystemV2ArrowChevronUpArrowUp } from '@doltech/icons/SystemV2/IconSystemV2ArrowChevronUpArrowUp';

const Container = styled.div`
  padding: 32px 20px;
  border-radius: 16px;
  border: 1px solid ${colorsV2.purple10};
  background-color: ${colorsV2.purple5};

  display: flex;
  flex-direction: column;
  align-items: center;
  ${verticalSpace(20)};

  ${fromScreen(768)} {
    align-items: flex-start;
  }
`;

const MediaWrapper = styled.div`
  border: 4px solid ${colorsV2.white100};
  border-radius: 300px;
  width: 160px;
  height: 160px;

  img {
    width: 100%;
    height: 100%;
    border-radius: 300px;
    object-fit: cover;
  }

  .empty-state {
    width: 100%;
    height: 100%;
    border-radius: 300px;
    background-color: ${colorsV2.gray10};
  }
`;

const TopWrapper = styled.div`
  display: flex;
  align-items: center;
  max-width: 500px;
  flex-direction: column;
  ${verticalSpace(8)};

  ${fromScreen(768)} {
    flex-direction: row;
    ${horizontalSpace(16)};
  }
`;

const BottomWrapper = styled.div`
  max-width: 500px;

  ${fromScreen(768)} {
    max-width: 100%;
    ${verticalSpace(16)};
  }
`;

const Info = styled.div`
  ${verticalSpace(12)};

  ${fromScreen(768)} {
    ${verticalSpace(16)};
  }
`;

const TextWrapper = styled.div`
  ${verticalSpace(8)};

  ${fromScreen(768)} {
    ${verticalSpace(12)};
  }
`;

const AuthorInfoWrapper = styled.div`
  width: max-content;
  display: flex;
  ${horizontalSpace(4)};
  border-radius: 20px;
  border: 1px solid ${colorsV2.purple10};
  background-color: ${colorsV2.white100};
`;

const FullDescription = styled.div`
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  gap: 8px;
`;

const LineDivider = styled.div`
  border-bottom: 1px solid ${colorsV2.neutral20};
  width: 100%;
`;

interface SectionAuthorInfoProps {
  avatarUrl?: string;
  authorName?: string;
  positionName?: string;
  totalPosts?: string;
  certificates?: AchievementItemProps[];
  otherDocuments?: AchievementItemProps[];
  degrees?: AchievementItemProps[];
  fullDescription?: string;
}

export const SectionAuthorInfoMobile = (props: SectionAuthorInfoProps) => {
  const {
    avatarUrl,
    authorName,
    positionName,
    totalPosts,
    certificates,
    otherDocuments,
    degrees,
    fullDescription,
  } = props;

  return (
    <Container className="session-author-info">
      <TopWrapper>
        <MediaWrapper>
          {avatarUrl ?
            <img src={avatarUrl} alt={authorName}/> :
            <div className="empty-state"/>
          }
        </MediaWrapper>
        <Info>
          <TextWrapper>
            <ResponsiveTypography.Title level={1} variant="bold/32-40" color="gray180">
              {authorName}
            </ResponsiveTypography.Title>
            <ResponsiveTypography.Paragraph variant="medium/16-24" color="gray160">
              {positionName}
            </ResponsiveTypography.Paragraph>
          </TextWrapper>
          <AuthorInfoWrapper>
            <IconSystemV2SpecificPenCurve size={20} color="purple100"/>
            <ResponsiveTypography.Paragraph variant="medium/16-24" color="purple100">
              Đóng góp: {totalPosts} bài viết
            </ResponsiveTypography.Paragraph>
          </AuthorInfoWrapper>
        </Info>
      </TopWrapper>
      <LineDivider/>
      <BottomWrapper>
        <AuthorAchievementInfo
          degrees={degrees}
          certificates={certificates}
          otherDocuments={otherDocuments}
        />
        <FullDescription>
          <ResponsiveTypography.Paragraph
            variant="regular/16-24"
            color="gray160"
            ellipsis={{
              rows: 3,
              expandable: true,
              symbol: (
                <TextLink
                  iconLeft={<IconSystemV2ArrowChevronDownArrowDown/>}
                  text="Xem thêm"
                  color="purple"
                  size="medium"
                />
              ),
              symbolNotExpand: (
                <TextLink
                  iconLeft={<IconSystemV2ArrowChevronUpArrowUp/>}
                  text="Thu gọn"
                  color="purple"
                  size="medium"
                />
              ),
            }}
          >
            {fullDescription}
          </ResponsiveTypography.Paragraph>
        </FullDescription>
      </BottomWrapper>
    </Container>
  );
};
